import * as React from "react"
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";

import { cn } from "@/lib/utils"

import { BorderBeam } from "@/components/ui/border-beam";

const Card = React.forwardRef(({ className, showBeam = false, showSpotlight = true, ...props }, ref) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div
      ref={ref}
      className={cn(
        "group relative rounded-xl border bg-card/60 dark:bg-card/40 backdrop-blur-md text-card-foreground shadow border-white/10",
        className
      )}
      onMouseMove={handleMouseMove}
      {...props}
    >
      {showBeam && (
        <BorderBeam size={250} duration={12} delay={9} colorFrom="#EA580C" colorTo="#fb923c" border_radius={12} />
      )}
      {showSpotlight && (
        <motion.div
          className="pointer-events-none absolute -inset-px z-20 rounded-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100"
          style={{
            background: useMotionTemplate`
                radial-gradient(
                250px circle at ${mouseX}px ${mouseY}px,
                rgba(234, 88, 12, 0.65),
                transparent 100%
                )
            `,
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            padding: "1.5px",
          }}
        />
      )}
      <div className="relative z-10 overflow-hidden rounded-[inherit]">{props.children}</div>
    </div>
  );
})
Card.displayName = "Card"

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props} />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props} />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props} />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
